import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, User, LogOut, Settings, Users, Search as SearchIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/utils';
import NotificationBell from './NotificationBell';

interface LayoutProps {
  children: React.ReactNode;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export default function Layout({ children, searchQuery = '', onSearchChange }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingFriendRequests, setPendingFriendRequests] = useState(0);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (user) {
      fetchPendingFriendRequests();

      // Set up subscription for real-time updates on all friend changes
      const friendsChannel = supabase
        .channel('friends-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'friends'
          },
          (payload) => {
            fetchPendingFriendRequests();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(friendsChannel);
      };
    }
  }, [user]);

  const fetchPendingFriendRequests = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('friends')
        .select('id')
        .eq('receiver_id', user.id)
        .eq('status', 'pending');

      if (error) {
        console.error('Error fetching friend requests:', error);
        return;
      }

      setPendingFriendRequests(data?.length || 0);
    } catch (err) {
      console.error('Error fetching friend requests:', err);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      setIsMenuOpen(false);

      // First clear any local storage data
      localStorage.clear();
      sessionStorage.clear();

      // Sign out locally first
      await supabase.auth.signOut();

      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Still try to redirect to login on error
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearchChange) {
      onSearchChange(localSearchQuery);
    }
  };

  const handleHomeClick = () => {
    setLocalSearchQuery('');
    setIsSearchExpanded(false);
    if (onSearchChange) {
      onSearchChange('');
    }
    navigate('/');
  };

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <div
                className="flex-shrink-0 flex items-center cursor-pointer"
                onClick={handleHomeClick}
                title="Your Feed"
              >
                <Home className={cn(
                  "h-8 w-8 transition-colors",
                  isActive('/') && !searchQuery ? "text-blue-500" : "text-gray-500 hover:text-blue-500"
                )} />
              </div>
              <div
                className="flex items-center cursor-pointer relative"
                onClick={() => navigate('/friends')}
                title="Your Friends"
              >
                <Users className={cn(
                  "h-8 w-8 transition-colors",
                  isActive('/friends') ? "text-blue-500" : "text-gray-500 hover:text-blue-500"
                )} />
                {pendingFriendRequests > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingFriendRequests > 9 ? '9+' : pendingFriendRequests}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {isActive('/') && (
                <div ref={searchRef} className="relative">
                  {!isSearchExpanded ? (
                    <button
                      onClick={handleSearchIconClick}
                      className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                      title="Search posts"
                    >
                      <SearchIcon className="h-6 w-6 text-gray-500" />
                    </button>
                  ) : (
                    <form onSubmit={handleSearchSubmit} className="flex items-center">
                      <div className="relative">
                        <input
                          type="text"
                          value={localSearchQuery}
                          onChange={(e) => setLocalSearchQuery(e.target.value)}
                          placeholder="Search posts..."
                          className="pl-3 pr-10 py-2 w-40 sm:w-48 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                          title="Search"
                        >
                          <SearchIcon className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
              <div title="Notifications">
                <NotificationBell />
              </div>
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={cn(
                    "p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                    isActive('/profile') && "text-blue-500"
                  )}
                  title="Your Account"
                >
                  <User className={cn(
                    "h-6 w-6 transition-colors",
                    isActive('/profile') ? "text-blue-500" : "text-gray-500"
                  )} />
                </button>

                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        role="menuitem"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Profile Settings
                      </button>
                      <button
                        onClick={handleLogout}
                        disabled={loading}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50"
                        role="menuitem"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {loading ? 'Signing out...' : 'Sign Out'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}
